import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import type { Job } from "@/types";

export type JobOutcome =
  | { kind: "running"; job: Job }
  | { kind: "completed"; job: Job }
  | { kind: "failed"; job: Job }
  | { kind: "error"; message: string };

export function useJobPolling(
  jobId: string | null,
  intervalMs = 1500,
): JobOutcome | null {
  const [outcome, setOutcome] = useState<JobOutcome | null>(null);
  const stopRef = useRef(false);

  useEffect(() => {
    if (!jobId) return;
    stopRef.current = false;

    const tick = async () => {
      if (stopRef.current) return;
      try {
        const { job } = await api.getJob(jobId);
        if (stopRef.current) return;
        if (job.status === "completed") {
          setOutcome({ kind: "completed", job });
          return;
        }
        if (job.status === "failed" || job.status === "cancelled") {
          setOutcome({ kind: "failed", job });
          return;
        }
        setOutcome({ kind: "running", job });
        setTimeout(tick, intervalMs);
      } catch (e) {
        if (stopRef.current) return;
        setOutcome({ kind: "error", message: (e as Error).message });
        setTimeout(tick, intervalMs * 2); // 錯誤時退避
      }
    };

    tick();

    return () => {
      stopRef.current = true;
    };
  }, [jobId, intervalMs]);

  return outcome;
}
